import { ReactNode, createContext, useEffect, useMemo } from 'react'
import { useCustomerState, useDateState, useLocationState, usePricingState, useRateState, useReservationState, useTimezoneState, useVehicleState } from '../utils/groupingState';
import moment from 'moment';
import { getAvailableCars } from '../services/availableCars';
import { getExtras } from '../services/additionalCharge';
import { getCustomer } from '../services/customer';
import { calculate, getReservationSummary } from '../services/rental';
import usePleaseWait from '../hooks/usePleaseWait';
import { tenantId } from '../config/api';


const ReservationContext = createContext({});

export const ReservationProvider = ({ children }: { children: ReactNode }) => {  
    const reservationState = useReservationState();
    const customerState = useCustomerState();
    const locationState = useLocationState();
    const dateState = useDateState();
    const timezoneState = useTimezoneState();
    const vehicleState = useVehicleState();
    const rateState = useRateState();
    const pricingState = usePricingState();

    const { setPleasWaitPopup }: any = usePleaseWait();

    useEffect(() => {
        if (locationState?.branches && timezoneState?.timezoneOptionsIsLoaded && parseInt(locationState?.branchId) > 0) {
            const branch = locationState?.branches?.find((item: any) => item?.id === parseInt(locationState?.branchId));
            if (branch) {
                const offset = moment.tz(moment.tz.guess()).format('Z');
                const defaultTimeZone = `(GMT ${offset}) ${moment.tz.guess()} ${moment.tz(moment.tz.guess()).zoneAbbr()}`;

                const branchTimezone = branch?.timeZone;
                if (branchTimezone) {
                    if (parseInt(branchTimezone) > 0) {
                        const timeZoneData = timezoneState?.timezoneOptions.find((item: any) => item.value === parseInt(branchTimezone));
                        if (timeZoneData) {

                            const timeData = new Date().toLocaleString("en-US", {
                                timeZone: timeZoneData?.label?.split(" ")[2]
                            });

                            dateState?.setPickupTime(moment(timeData).format('HH:mm'));
                            dateState?.setReturnTime(moment(timeData).format('HH:mm'));

                            dateState?.setFormattedCreatedAt(timeZoneData?.label);
                        } else {

                            const timeData = new Date().toLocaleString("en-US", {
                                timeZone: moment.tz.guess()
                            });

                            dateState?.setPickupTime(moment(timeData).format('HH:mm'));
                            dateState?.setReturnTime(moment(timeData).format('HH:mm'));


                            dateState?.setFormattedCreatedAt(defaultTimeZone);
                        }
                    } else {

                        const timeData = new Date().toLocaleString("en-US", {
                            timeZone: moment.tz.guess()
                        });

                        dateState?.setPickupTime(moment(timeData).format('HH:mm'));
                        dateState?.setReturnTime(moment(timeData).format('HH:mm'));


                        dateState?.setFormattedCreatedAt(defaultTimeZone);
                    }
                } else {
                    dateState?.setFormattedCreatedAt(defaultTimeZone);
                }
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationState?.branchId, locationState?.branches, timezoneState?.timezoneOptionsIsLoaded]);

    const triggerSummaryCalculation = (
        inputPickupDate: string,
        inputPickupTime: string,
        inputReturnDate: string,
        inputReturnTime: string,
        inputBranchId: number,
        inputPickupLocationId: number,
        inputReturnLocationId: number,
        inputWalkInCustomer: boolean,
        mandatoryAdditionalChargesAmount: number,
        locationFeeAmount: number,
        taxAmount: number,
        governmentTaxAmount: number
    ) => {
        let additionalValue: any[] = [];
        if (pricingState?.checkExtraValue?.length > 0) {
            pricingState?.checkExtraValue?.forEach((item: any,) => {
                additionalValue = [...additionalValue, ...item?.additionalCharges];
            });
        }

        let latestPickup = moment().format('YYYY-MM-DDTHH:mm');
        let latestReturn = moment().format('YYYY-MM-DDTHH:mm');
        if (inputPickupDate && inputPickupTime) latestPickup = moment(`${inputPickupDate}T${inputPickupTime}`).format('YYYY-MM-DDTHH:mm');
        if (inputReturnDate && inputReturnTime) latestReturn = moment(`${inputReturnDate}T${inputReturnTime}`).format('YYYY-MM-DDTHH:mm');

        calculate(
            {
                pickupDate: latestPickup,
                returnDate: latestReturn,
                branchId: inputBranchId ?? 0,
                pickupLocationId: inputPickupLocationId ?? 0,
                returnLocationId: inputReturnLocationId ?? 0,
                isWalkInCustomer: inputWalkInCustomer,
                vehicleClassId: vehicleState?.vehicleClassId ?? 0,
                wasSecurityDepositManuallyChanged: false,
                securityDepositAmount: 0,
                reservationAdditionalChargeDto: additionalValue?.map((item: any) => (
                    {
                        additionalChargeByBranchId: item?.additionalChargeBranchId,
                        quantity: item?.quantity,
                        basePrice: item?.basePrice
                    }
                )),
                reservationDiscountDto: [],
                mandatoryAdditionalChargesAmount: mandatoryAdditionalChargesAmount,
                locationFeePercentage: locationFeeAmount ?? 0,
                taxPercentage: taxAmount ?? 0,
                governmentTaxPercentage: governmentTaxAmount ?? 0,
                customerId: customerState?.customerId ?? 0,
                localAddress: "",
                fileUploadDataDto: []
            },
            tenantId
        )
            .then(data => {
                setPleasWaitPopup(false);
                reservationState?.setReservation(data);
            })
            .catch((error) => {
                setPleasWaitPopup(false);
                reservationState?.setReservation(null);
                console.log(error);
            })
    }

    const getDataFromCars = (
        vehicleClassId: string,
        selectedVehicleClass: any
    ) => {
        vehicleState?.setVehicleClassId(vehicleClassId);
        vehicleState?.setSelectedVehicleClass(selectedVehicleClass);
    };

    //calculate the summary without reservation
    useEffect(() => {
        if (reservationState?.primaryKey <= 0 && parseInt(locationState?.branchId) > 0) {
            setPleasWaitPopup(true);

            triggerSummaryCalculation(
                dateState?.pickupDate,
                dateState?.pickupTime,
                dateState?.returnDate,
                dateState?.returnTime,
                parseInt(locationState?.branchId),
                parseInt(locationState?.pickupLocationId),
                parseInt(locationState?.returnLocationId),
                false,
                pricingState?.mandatoryAdditionalChargesAmount,
                locationState?.locationFeeAmount,
                pricingState?.taxAmount,
                pricingState?.governmentTaxAmount
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dateState?.pickupDate,
        dateState?.pickupTime,
        dateState?.returnDate,
        dateState?.returnTime,
        locationState?.branchId,
        locationState?.pickupLocationId,
        locationState?.returnLocationId,
        pricingState?.mandatoryAdditionalChargesAmount,
        locationState?.locationFeeAmount,
        pricingState?.taxAmount,
        pricingState?.governmentTaxAmount,
        vehicleState?.vehicleClassId,
        customerState?.customerId,
        pricingState?.checkExtraValue
    ])

    const updateResevation = () => {
        if (parseInt(locationState?.branchId) > 0 && dateState?.pickupDate && dateState?.pickupTime && dateState?.returnDate && dateState?.returnTime) {
            let latestPickup = moment().format('YYYY-MM-DDTHH:mm');
            let latestReturn = moment().format('YYYY-MM-DDTHH:mm');

            if (dateState?.pickupDate) latestPickup = moment(`${dateState.pickupDate}T${dateState.pickupTime}`).format('YYYY-MM-DDTHH:mm');
            if (dateState?.returnDate) latestReturn = moment(`${dateState.returnDate}T${dateState.returnTime}`).format('YYYY-MM-DDTHH:mm');

            getAvailableCars({
                pickupDate: latestPickup,
                returnDate: latestReturn,
                branchId: parseInt(locationState?.branchId)
            }, tenantId)
                .then((data) => {
                    vehicleState?.setAvailableVehicle(data);
                    vehicleState?.setSelectedVehicleClass(data?.find((item: any) => item?.vehicleClassId === parseInt(vehicleState?.vehicleClassId)));

                    getDataFromCars(
                        data?.find((item: any) => item?.vehicleClassId === parseInt(vehicleState?.vehicleClassId))?.vehicleClassId,
                        data?.find((item: any) => item?.vehicleClassId === parseInt(vehicleState?.vehicleClassId))
                    );
                })
                .catch((error) => {
                    console.error(error);
                });
        }
    }

    useEffect(() => {
        updateResevation()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationState?.branchId, dateState?.pickupDate, dateState?.pickupTime, dateState?.returnDate, dateState?.returnTime]);

    useEffect(() => {
        if (parseInt(locationState?.branchId) > 0 && parseInt(vehicleState?.vehicleClassId) > 0) {
            pricingState?.setExtraValueLoaded(false);
            getExtras(locationState?.branchId, vehicleState?.vehicleClassId, tenantId)
                .then((data: any) => {
                    pricingState?.setExtraValue(data);
                    pricingState?.setExtraValueLoaded(true);
                })
                .catch((error) => console.error(error));
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationState?.branchId, vehicleState?.vehicleClassId]);

    useEffect(() => {
        if (reservationState?.currentPage === 6) {
            getReservationData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservationState?.currentPage])

    const getReservationData = () => {
        getReservationSummary(reservationState?.primaryKey, tenantId)
            .then((data) => {
                reservationState?.setReservationDataFromCofirm(data)
            })
            .catch((error) => {
                console.error(error);
                reservationState?.setReservationDataFromCofirm(null);
            })
    }

    const getDataFromExtras = (updatedCheckExtraValue: any[]) => {
        pricingState?.setCheckExtraValue(updatedCheckExtraValue);
    };

    const getDataFromCustomer = (customerId: number, localAddress: string) => {
        customerState?.setLocalAddress(localAddress);
        customerState?.setCustomerId(customerId);
        if (customerState?.customerId) {
            getCustomer(customerState?.customerId, tenantId)
                .then((data) => {
                    customerState?.setCustomerData(data);
                })
                .catch((error) => {
                    customerState?.setCustomerData(null);
                    console.error(error);
                });
        }
    };

    useEffect(() => {
        if (customerState?.customerId) {
            getCustomer(customerState?.customerId, tenantId)
                .then((data) => {
                    customerState?.setCustomerData(data);
                })
                .catch((error) => {
                    customerState?.setCustomerData(null);
                    console.error(error);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerState?.customerId]);


    const value = useMemo(
        () => ({
            ...reservationState,
            ...customerState,
            ...locationState,
            ...dateState,
            ...timezoneState,
            ...vehicleState,
            ...rateState,
            ...pricingState,
            getDataFromCars,
            getDataFromExtras,
            getDataFromCustomer,
            updateResevation
        }),
        //eslint-disable-next-line react-hooks/exhaustive-deps
        [
            reservationState,
            customerState,
            locationState,
            dateState,
            timezoneState,
            vehicleState,
            rateState,
            pricingState
        ]
    )

    return (
        <ReservationContext.Provider value={value}>
            {children}
        </ReservationContext.Provider>
    )
}

export default ReservationContext;